import React, { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { ShoppingCart, Menu, X, Store, LogOut } from "lucide-react";
import Logo, { FullLogo } from "./Logo";
import { useApp } from "../context/AppContext";

const Navbar = () => {
  const { t, lang, toggleLang, cartCount, setCartOpen, client, logoutClient } = useApp();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setMobileOpen(false); }, [location]);

  const links = [
    { to: "/catalogue", label: t("nav_catalogue") }
  ];

  return (
    <header className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${scrolled ? "bg-white/95 backdrop-blur-md shadow-sm py-2" : "bg-transparent py-4"}`}>
      <div className="max-w-7xl mx-auto px-5 flex items-center justify-between">
        <Link to="/" aria-label="Ssuq"><FullLogo className="h-14 md:h-16 w-auto" /></Link>

        <nav className="hidden md:flex items-center gap-8">
          {links.map((l) => (
            <Link key={l.to} to={l.to}
              className={`font-display text-sm font-semibold transition-colors hover:text-orange ${location.pathname === l.to ? "text-orange" : "text-navy"}`}>
              {l.label}
            </Link>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <button onClick={toggleLang}
            className="font-display text-sm font-bold text-navy border border-navy/30 rounded-full px-3 py-1.5 hover:bg-navy hover:text-white transition-colors">
            {lang === "fr" ? "عربي" : "FR"}
          </button>

          <button onClick={() => setCartOpen(true)} className="relative p-2 text-navy hover:text-orange transition-colors" aria-label="Panier">
            <ShoppingCart size={22} />
            {cartCount > 0 && (
              <span className="absolute -top-1 ltr:-right-1 rtl:-left-1 bg-orange text-white text-[10px] font-bold rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1">{cartCount}</span>
            )}
          </button>

          {client ? (
            <div className="hidden sm:inline-flex items-center gap-2 bg-navy/5 rounded-full pl-3 pr-1.5 py-1" data-testid="client-badge">
              <Link to="/mes-commandes" data-testid="my-orders-link" className="font-display font-semibold text-xs text-navy hover:text-orange max-w-[140px] truncate">{client.company} · Mes commandes</Link>
              <button onClick={logoutClient} data-testid="client-logout-btn" title="Déconnexion" className="w-7 h-7 rounded-full bg-white text-navy hover:text-orange flex items-center justify-center"><LogOut size={14} /></button>
            </div>
          ) : (
            <button onClick={() => navigate("/inscription")}
              className="hidden sm:inline-flex btn-orange font-display text-sm font-semibold rounded-full px-5 py-2.5 items-center gap-2">
              <Store size={16} /> {t("nav_compte")}
            </button>
          )}

          <button className="md:hidden p-2 text-navy" onClick={() => setMobileOpen((v) => !v)} aria-label="Menu">
            {mobileOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {mobileOpen && (
        <div className="md:hidden bg-white border-t border-gray-100 shadow-lg">
          <div className="px-5 py-4 flex flex-col gap-3">
            {links.map((l) => (
              <Link key={l.to} to={l.to} className="font-display font-semibold text-navy py-1">{l.label}</Link>
            ))}
            <Link to="/connexion" className="font-display font-semibold text-navy py-1">{t("nav_connexion")}</Link>
            <button onClick={() => navigate("/inscription")} className="btn-orange font-display font-semibold rounded-full px-5 py-2.5 mt-1">{t("nav_compte")}</button>
          </div>
        </div>
      )}
    </header>
  );
};

export default Navbar;
